import { useState } from "react";

const P = {
  cream: "#F6F1E9", warm: "#EDE5D4", slate: "#252D3D", slateLight: "#3A4460",
  accent: "#C8773A", green: "#2E7D57", red: "#B03A3A", muted: "#8A90A8",
  border: "#D8CFBE", white: "#FFFFFF", gold: "#C8A43A",
};

const SECTIONS = [
  {
    id: "getting-started",
    icon: "🚀",
    title: "Getting Started",
    content: [
      {
        heading: "Welcome to Groundwork!!",
        text: "Groundwork is YOUR personal budget builder. No spreadsheets, no confusing finance apps — just a simple, clear picture of where your money goes every month.",
      },
      {
        heading: "Step 1 — Tell us about you",
        text: "On the opening screen, enter your name and your monthly take-home pay. That's the money that actually hits your bank account after taxes. You can even track ALLOWANCE here. Not sure of the exact amount? Enter $0 for now — you can update it anytime by tapping the ✎ pencil next to your income in the banner at the top!",
      },
      {
        heading: "Step 2 — Add your photo",
        text: "Tap the photo circle and upload a pic — your gamer tag, your dog, your favorite selfie — whatever makes this feel like YOURS! You can change it anytime by tapping your photo in the top left corner.",
      },
      {
        heading: "Step 3 — Start adding expenses",
        text: "Tap '➕ Add Item to Budget' at the top to start browsing expense categories. Tap any item to add it to your budget. Don't see something? Add it yourself using the Custom Expense section at the bottom of any category!",
      },
      {
        heading: "Step 4 — Check your Budget Sheet",
        text: "Tap '📋 Go To Budget Sheet' at the top to see your full budget. You'll see every expense organized by category, your total monthly outflow, and whether you're in the green (surplus) or the red (deficit) at a glance!",
      },
    ],
  },
  {
    id: "adding-expenses",
    icon: "💰",
    title: "Adding Expenses",
    content: [
      {
        heading: "Browsing Categories",
        text: "Tap any category card to see a list of common expenses in that category. Each one shows a suggested monthly amount to get you started. Tap any item to add it — tap it again to remove it!",
      },
      {
        heading: "Suggested Amounts",
        text: "The suggested amounts are just starting points based on national averages. They won't be perfect for YOUR situation — and that's OK! Head to your Budget Sheet and tap any amount to edit it to what YOU actually spend.",
      },
      {
        heading: "Adding Custom Expenses",
        text: "Don't see something you spend money on? Scroll to the bottom of any category page and use the Custom Expense section. Type the name, enter the amount, choose how often you pay it (monthly, weekly, bi-weekly or yearly) and tap Add!",
      },
      {
        heading: "Weekly or Bi-weekly expenses",
        text: "Some expenses happen every week or every two weeks — like a weekly grocery run. Select the right frequency when adding the expense and Groundwork automatically converts it to a monthly amount so everything stays consistent on your Budget Sheet!",
      },
      {
        heading: "The green checkmark",
        text: "When an item is added to your budget it turns green with a ✓ Added checkmark. That means it's on your Budget Sheet! Tap it again to remove it if you change your mind.",
      },
    ],
  },
  {
    id: "budget-sheet",
    icon: "📋",
    title: "Your Budget Sheet",
    content: [
      {
        heading: "What is the Budget Sheet?",
        text: "The Budget Sheet is your complete monthly budget in one place. Every expense you added is organized by category, with subtotals per category and a grand total at the bottom. Think of it as your financial snapshot!",
      },
      {
        heading: "The banner at the top",
        text: "The colored banner always shows your Monthly Income, Total Out, and your Surplus or Deficit. Green means you're keeping more than you're spending — great job!! Red means you're spending more than you make — time to make some adjustments!",
      },
      {
        heading: "Editing amounts",
        text: "See an amount that doesn't match what you actually spend? Tap the amount on the Budget Sheet and it turns into an editable field. Type your real number and hit Enter or tap away — it updates instantly!",
      },
      {
        heading: "Collapsing categories",
        text: "Got a long budget sheet? Tap any category header to collapse it and hide the items inside. Tap it again to expand! This makes it easy to navigate without scrolling forever on your phone.",
      },
      {
        heading: "Editing your income",
        text: "Tap the ✎ pencil button right next to your income amount in the banner to jump straight to the income screen and update it. Quick and easy — no hunting around!",
      },
    ],
  },
  {
    id: "saving-goals",
    icon: "🎯",
    title: "Saving Goals",
    content: [
      {
        heading: "What are Saving Goals?",
        text: "Saving Goals are separate from your regular expenses because saving money is something to CELEBRATE — not just another bill! Whether it's an emergency fund, a vacation or a new phone — give your savings a name and a target!",
      },
      {
        heading: "Setting a Target Amount",
        text: "When you add a Saving Goal, enter the total amount you're trying to reach. For example — if you want to save $1,200 for a vacation, enter $1,200 as your target.",
      },
      {
        heading: "Monthly Set-Aside",
        text: "This is how much you're committing to put away EACH MONTH toward your goal. Groundwork automatically calculates how many months it will take to reach your target based on this amount!",
      },
      {
        heading: "The countdown",
        text: "Once you enter a target AND a monthly amount you'll see a message like '⏱ At $200/mo you'll hit your goal in ~6 months' — that's your motivation right there!! Watch that number shrink as you increase your monthly set-aside!",
      },
      {
        heading: "Custom Goals",
        text: "Don't see your goal in the list? Scroll to the bottom of the Saving Goals page and add a Custom Goal — name it whatever you want! Gaming PC, Wedding Fund, Moving Out Fund — it's YOUR goal!!",
      },
    ],
  },
  {
    id: "pay-off-debt",
    icon: "💳",
    title: "Pay Off Debt",
    content: [
      {
        heading: "Why is Debt separate?",
        text: "Paying off debt is different from a regular monthly expense — it has a FINISH LINE! Groundwork keeps it separate so you can see exactly what you owe, what you're paying monthly, and how long until it's GONE!",
      },
      {
        heading: "Total Balance",
        text: "When you add a debt, enter the TOTAL amount you currently owe. For example — if your credit card balance is $2,400, enter $2,400. This is your starting point!",
      },
      {
        heading: "Monthly Payment",
        text: "Enter how much you're paying toward this debt each month. Groundwork then calculates roughly how many months until it's paid off — at the current payment amount!",
      },
      {
        heading: "The payoff countdown",
        text: "You'll see something like '🏁 At $200/mo you'll pay this off in ~12 months' — that finish line is POWERFUL motivation to keep making those payments!! Try increasing your monthly payment and watch the months drop!!",
      },
      {
        heading: "Custom Debts",
        text: "Don't see your debt type in the list? Scroll down and add a Custom Debt — name it whatever makes sense to you. Buy Now Pay Later, Rent to Own, Money I owe my parents 😄 — whatever it is, track it here!",
      },
    ],
  },
  {
    id: "saving-budget",
    icon: "💾",
    title: "Saving Your Budget",
    content: [
      {
        heading: "Auto-save vs Manual Save",
        text: "Groundwork automatically saves your budget to your device every time you make a change — so you won't lose your work if you close the app! BUT — this auto-save only works on THIS device in THIS browser.",
      },
      {
        heading: "The green Save button",
        text: "See the 💾 Save button in the top right? It glows GREEN when you have unsaved changes that haven't been manually saved yet. Tap it to make sure everything is locked in! When it goes grey — you're all saved up!",
      },
      {
        heading: "Why should I manually save?",
        text: "The auto-save stores your budget in your browser's local storage. If you ever clear your browser history or cache — that data could be lost! Manual saving + Export gives you a real backup you control.",
      },
      {
        heading: "Switching devices",
        text: "Auto-save only works on the device and browser you used to build your budget. If you want your budget on your phone AND your laptop — use the Export feature to save a backup file and Restore it on the other device!",
      },
    ],
  },
  {
    id: "export-restore",
    icon: "📁",
    title: "Export & Restore",
    content: [
      {
        heading: "What is Export?",
        text: "Export saves your ENTIRE budget — every expense, every goal, every debt — as a file on your device. Think of it as making a photocopy of your budget that you can store safely and use to restore later!",
      },
      {
        heading: "How to Export",
        text: "Go to your Budget Sheet and tap '📁 Export My Budget'. Your device will save a file called something like 'groundwork-budget-YourName.json'. Check your Downloads folder on a laptop or your Files app on iPhone to find it!",
      },
      {
        heading: "Where does the file go?",
        text: "On a laptop it usually goes straight to your Downloads folder. On iPhone it shows a share sheet — tap 'Save to Files' to keep it in your Files app. On Android it typically goes to your Downloads folder. Save it somewhere you'll remember!!",
      },
      {
        heading: "How to Restore",
        text: "Lost your budget or switching devices? Tap '📂 Restore My Budget' on the opening screen or Budget Sheet. Find your exported file, tap it, and your entire budget comes right back — exactly as you left it!!",
      },
      {
        heading: "Export often!!",
        text: "Get into the habit of exporting your budget after every major update — just like saving a document! It takes 2 seconds and means you'll NEVER lose your budget data no matter what happens to your device or browser.",
      },
    ],
  },
  {
    id: "pin-protection",
    icon: "🔒",
    title: "PIN Protection",
    content: [
      {
        heading: "What is PIN Protection?",
        text: "PIN Protection locks your budget behind a 4-8 digit PIN so nosy roommates, siblings or anyone else can't snoop on your finances! Your budget is YOUR business!!",
      },
      {
        heading: "Setting up your PIN",
        text: "Tap '🔒 Set My PIN' on the opening screen or find the PIN option on your Budget Sheet. Choose a 4-8 digit PIN, confirm it, and you're locked up! Don't forget your PIN — there's no recovery option yet since we don't store your email!",
      },
      {
        heading: "PIN Timeout",
        text: "You can choose how long before Groundwork asks for your PIN again after you leave the app. Options range from 1 minute to 30 minutes — or only when you completely close and reopen the app. Pick what works for YOUR lifestyle!",
      },
      {
        heading: "Removing your PIN",
        text: "Want to remove PIN protection? Go to the Budget Sheet, tap the 🔒 PIN button in the toolbar, and tap 'Remove PIN'. You'll need to enter your current PIN first to confirm it's really you!",
      },
      {
        heading: "Forgot your PIN?",
        text: "Right now there's no PIN recovery option — so don't forget it!! Write it somewhere safe. In a future update we'll add account-based recovery. For now — choose something memorable but not obvious!!",
      },
    ],
  },
  {
    id: "miscellaneous",
    icon: "📦",
    title: "Miscellaneous",
    content: [
      {
        heading: "What is Miscellaneous?",
        text: "Miscellaneous is your catch-all category for expenses that don't obviously fit anywhere else — or when you just want to add something FAST without thinking about where it belongs! Just get it in the budget first, organize it later!!",
      },
      {
        heading: "Adding to Miscellaneous",
        text: "Use the '➕ Add a Custom Expense' section on the main categories screen. Whatever you add there automatically goes into Miscellaneous. You'll see it on your Budget Sheet under the 📦 Miscellaneous section!",
      },
      {
        heading: "Moving to a category",
        text: "Once you're ready to organize — go to your Budget Sheet and find the Miscellaneous section. Tap the amber ↗ button next to any item. A menu pops up showing all your categories — tap the right one and it moves instantly!!",
      },
      {
        heading: "The amber hint",
        text: "The Miscellaneous category header shows a small note '· tap ↗ to organize' — that's your reminder that items in here are waiting to find their home! Try to move them to proper categories when you have a moment.",
      },
      {
        heading: "It's OK to leave things here!",
        text: "Some expenses genuinely don't fit neatly into any category — and that's totally fine!! Miscellaneous exists for exactly that reason. Your budget still counts everything correctly whether it's categorized or not!!",
      },
    ],
  },
];

export default function HelpMe({ onClose }) {
  const [currentSection, setCurrentSection] = useState(0);
  const [currentContent, setCurrentContent] = useState(0);

  const section = SECTIONS[currentSection];
  const content = section.content[currentContent];
  const isFirstSection = currentSection === 0;
  const isLastSection = currentSection === SECTIONS.length - 1;
  const isFirstContent = currentContent === 0;
  const isLastContent = currentContent === section.content.length - 1;

  function nextContent() {
    if (!isLastContent) { setCurrentContent(prev => prev + 1); }
    else if (!isLastSection) { setCurrentSection(prev => prev + 1); setCurrentContent(0); }
  }

  function prevContent() {
    if (!isFirstContent) { setCurrentContent(prev => prev - 1); }
    else if (!isFirstSection) { setCurrentSection(prev => prev - 1); setCurrentContent(SECTIONS[currentSection - 1].content.length - 1); }
  }

  function jumpToSection(index) {
    setCurrentSection(index);
    setCurrentContent(0);
  }

  return (
    <div style={S.overlay}>
      <div style={S.screen}>

        {/* ── HEADER ── */}
        <div style={S.header}>
          <div style={S.headerLeft}>
            <span style={S.helpIcon}>❓</span>
            <span style={S.headerTitle}>Help Me</span>
          </div>
          <button style={S.closeBtn} onClick={onClose}>✕ Close</button>
        </div>

        {/* ── SECTION ICON MENU ── */}
        <div style={S.iconMenu}>
          {SECTIONS.map((s, i) => (
            <button key={s.id} style={{
              ...S.iconBtn,
              background: i === currentSection ? P.accent : "transparent",
              borderColor: i === currentSection ? P.accent : P.border,
            }} onClick={() => jumpToSection(i)}>
              <span style={S.iconBtnIcon}>{s.icon}</span>
              <span style={{
                ...S.iconBtnLabel,
                color: i === currentSection ? P.white : P.muted,
              }}>{s.title}</span>
            </button>
          ))}
        </div>

        {/* ── CONTENT ── */}
        <div style={S.content}>
          {/* Section title */}
          <div style={S.sectionTitle}>
            <span style={S.sectionIcon}>{section.icon}</span>
            <span style={S.sectionTitleText}>{section.title}</span>
          </div>

          {/* Content card */}
          <div style={S.contentCard}>
            <h3 style={S.contentHeading}>{content.heading}</h3>
            <p style={S.contentText}>{content.text}</p>

            {/* Content dots */}
            <div style={S.dots}>
              {section.content.map((_, i) => (
                <div key={i} style={{
                  ...S.dot,
                  background: i === currentContent ? P.accent : P.border,
                  width: i === currentContent ? "20px" : "8px",
                }} onClick={() => setCurrentContent(i)} />
              ))}
            </div>
          </div>

          {/* Progress */}
          <p style={S.progress}>
            Section {currentSection + 1} of {SECTIONS.length} · Topic {currentContent + 1} of {section.content.length}
          </p>
        </div>

        {/* ── NAVIGATION ── */}
        <div style={S.nav}>
          <button
            style={{
              ...S.navBtn,
              opacity: isFirstSection && isFirstContent ? 0.3 : 1,
              cursor: isFirstSection && isFirstContent ? "default" : "pointer",
            }}
            onClick={prevContent}
            disabled={isFirstSection && isFirstContent}>
            ← Previous
          </button>
          <button style={S.closeNavBtn} onClick={onClose}>
            Back to App
          </button>
          <button
            style={{
              ...S.navBtn,
              opacity: isLastSection && isLastContent ? 0.3 : 1,
              cursor: isLastSection && isLastContent ? "default" : "pointer",
              textAlign: "right",
            }}
            onClick={nextContent}
            disabled={isLastSection && isLastContent}>
            Next →
          </button>
        </div>

      </div>
    </div>
  );
}

const S = {
  overlay: {
    position: "fixed", inset: 0, background: "rgba(37,45,61,0.85)",
    zIndex: 100, display: "flex", alignItems: "stretch", justifyContent: "center",
    fontFamily: "'Georgia','Times New Roman',serif",
  },
  screen: {
    background: P.cream, width: "100%", maxWidth: "640px",
    display: "flex", flexDirection: "column", overflow: "hidden",
  },
  header: {
    background: P.slate, padding: "14px 16px",
    display: "flex", alignItems: "center", justifyContent: "space-between",
    flexShrink: 0,
  },
  headerLeft: { display: "flex", alignItems: "center", gap: "10px" },
  helpIcon: {
    width: "32px", height: "32px", background: P.white,
    borderRadius: "50%", display: "flex", alignItems: "center",
    justifyContent: "center", fontSize: "24px", border:"3px solid #f3064d",
    lineHeight: "32px", textAlign: "center",
  },
  headerTitle: { color: P.cream, fontSize: "18px", fontWeight: "700" },
  closeBtn: {
    background: "rgba(255,255,255,0.15)", border: "none",
    color: P.cream, fontSize: "13px", fontWeight: "700",
    cursor: "pointer", padding: "6px 12px", borderRadius: "6px",
    fontFamily: "inherit",
  },
  iconMenu: {
    display: "flex", gap: "6px", padding: "12px",
    overflowX: "auto", background: P.warm,
    borderBottom: `1px solid ${P.border}`, flexShrink: 0,
  },
  iconBtn: {
    display: "flex", flexDirection: "column", alignItems: "center",
    gap: "3px", padding: "8px 10px", border: "1.5px solid",
    borderRadius: "10px", cursor: "pointer", flexShrink: 0,
    fontFamily: "inherit", minWidth: "60px", transition: "all 0.2s",
  },
  iconBtnIcon: { fontSize: "20px" },
  iconBtnLabel: { fontSize: "9px", fontWeight: "700", textAlign: "center", lineHeight: 1.2 },
  content: {
    flex: 1, padding: "20px 16px", overflowY: "auto",
    display: "flex", flexDirection: "column", gap: "14px",
  },
  sectionTitle: {
    display: "flex", alignItems: "center", gap: "10px",
    paddingBottom: "12px", borderBottom: `2px solid ${P.border}`,
  },
  sectionIcon: { fontSize: "28px" },
  sectionTitleText: { fontSize: "22px", fontWeight: "900", color: P.slate, letterSpacing: "-0.5px" },
  contentCard: {
    background: P.white, borderRadius: "14px",
    padding: "20px", border: `1px solid ${P.border}`,
    boxShadow: "0 2px 12px rgba(37,45,61,0.06)",
    flex: 1,
  },
  contentHeading: {
    fontSize: "17px", fontWeight: "700", color: P.slate,
    margin: "0 0 12px", letterSpacing: "-0.3px",
  },
  contentText: {
    fontSize: "15px", color: "#444", lineHeight: 1.7,
    margin: "0 0 20px",
  },
  dots: { display: "flex", gap: "6px", alignItems: "center", justifyContent: "center" },
  dot: {
    height: "8px", borderRadius: "4px",
    cursor: "pointer", transition: "all 0.2s",
  },
  progress: {
    fontSize: "11px", color: P.muted, textAlign: "center",
    margin: 0, fontStyle: "italic",
  },
  nav: {
    display: "flex", gap: "10px", padding: "14px 16px",
    background: P.warm, borderTop: `1px solid ${P.border}`,
    flexShrink: 0, alignItems: "center",
  },
  navBtn: {
    flex: 1, padding: "12px", background: P.white,
    border: `1.5px solid ${P.border}`, borderRadius: "10px",
    color: P.slate, fontSize: "14px", fontWeight: "700",
    fontFamily: "inherit",
  },
  closeNavBtn: {
    flex: 1, padding: "12px", background: P.slate,
    border: "none", borderRadius: "10px",
    color: P.cream, fontSize: "14px", fontWeight: "700",
    cursor: "pointer", fontFamily: "inherit", textAlign: "center",
  },
};
